import React, { useState, useEffect } from "react";
import axios from "axios";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import styles from "./ProductTrial.module.css";
import API_BASE_URL from "../../config/api";

const ProductTrial = () => {
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    remarks: "",
    location: "",
    date: "",
    timeSlot: "",
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookedSlots, setBookedSlots] = useState({});

  // Predefined time slots
  const timeSlots = [
    "10:00 AM - 11:00 AM",
    "11:30 AM - 12:30 PM",
    "1:00 PM - 2:00 PM",
    "2:30 PM - 3:30 PM",
    "4:00 PM - 5:00 PM",
    "5:30 PM - 6:30 PM",
    "7:00 PM - 8:00 PM",
    "8:30 PM - 9:30PM",
  ];

  const handleTimeSlotDisable = (slot) => {
    const currentDate = new Date();
    const selectedDate = new Date(form.date);

    if (selectedDate.toDateString() === currentDate.toDateString()) {
      // Get current time in 24-hour format for comparison
      const currentHours = currentDate.getHours();
      const currentMinutes = currentDate.getMinutes();

      // Convert slot to 24-hour format for comparison
      const [startTime] = slot.split(" - ");
      let [hours, minutes] = startTime.split(":");
      const period = startTime.includes("PM") ? "PM" : "AM";

      // Convert to 24-hour format
      hours = parseInt(hours, 10);
      if (period === "PM" && hours !== 12) hours += 12;
      if (period === "AM" && hours === 12) hours = 0;
      minutes = parseInt(minutes, 10);

      // Disable past time slots for today
      return (
        hours < currentHours ||
        (hours === currentHours && minutes < currentMinutes)
      );
    }

    return false; // Future dates have all slots available
  };

  // Fetch booked time slots for the selected date and location
  const fetchBookedTimeSlots = async (date, location) => {
    if (!date || !location) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/getBookedTimeSlots.php?date=${date}&location=${location}`
      );
      setBookedSlots(response.data || {}); // Ensure valid data
    } catch (error) {
      console.error("Error fetching booked time slots:", error);
      setBookedSlots({}); // Reset in case of an error
    }
  };

  // Fetch locations from API
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/getStoreLocation.php`)
      .then((response) => {
        const storeLocations = response.data.stores || [];
        setLocations(storeLocations);

        // Set default location to "The Gardens"
        const defaultLocation = storeLocations.find(
          (location) => location.name.toLowerCase() === "the gardens"
        );

        if (defaultLocation) {
          setForm((prevForm) => ({
            ...prevForm,
            location: defaultLocation.id,
          }));
        }
      })
      .catch((error) => {
        console.error("Error fetching locations:", error);
      });
  }, []);

  // Fetch booked slots when date or location changes
  useEffect(() => {
    if (form.date && form.location) {
      fetchBookedTimeSlots(form.date, form.location);
    }
  }, [form.date, form.location]);

  const handleInputChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handlePhoneChange = (value) => {
    setForm({ ...form, phone: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);

    axios
      .post(`${API_BASE_URL}/handleAppointmentSubmission.php`, form)
      .then(() => {
        setFormSubmitted(true);
        setForm({
          name: "",
          email: "",
          phone: "",
          remarks: "",
          location: "",
          date: "",
          timeSlot: "",
        });
      })
      .catch((error) => {
        console.error("Error submitting form:", error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <div>
      <div 
        className={styles.productTrialLocation_container}
        style={{backgroundImage: `url(${API_BASE_URL}/uploads/headerImg/bodyfriend_outlet.jpg)`}}
      >
        <h1 className={styles.title}>Product Trial</h1>
        <p className={styles.subtitle}>
          Visit Bodyfriend store to experience our <b>Massage Chair</b> now.
        </p>
      </div>
      <div className={styles.appointmentForm_container}>
        {formSubmitted ? (
          <p className={styles.successMessage}>
            Thank you! Your appointment request has been submitted. We will
            revert back to you shortly!
          </p>
        ) : (
          <form onSubmit={handleSubmit} className={styles.appointmentForm}>
            <div className={styles.formText}>
              <h2>Book an Appointment with us.</h2>
              <span>
                Leave your contact details and we'll get back to you shortly.
              </span>
            </div>
            {/* Two-column row: Name & Phone */}
            <div className={styles.row}>
              <div className={styles.inputGroup}>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleInputChange}
                  placeholder="Enter Your Name *"
                  required
                />
              </div>
              <div className={styles.inputGroup}>
                <PhoneInput
                  country={"my"}
                  value={form.phone}
                  onChange={handlePhoneChange}
                  inputProps={{ name: "phone", required: true }}
                  containerClass={styles.phoneContainer}
                  inputClass={styles.phoneInput}
                />
              </div>
            </div>

            {/* Email */}
            <div className={styles.inputGroup}>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleInputChange}
                placeholder="Enter Your Email *"
                required
              />
            </div>

            {/* Location */}
            <div className={styles.inputGroup}>
              <select
                name="location"
                value={form.location}
                onChange={handleInputChange}
                required
              >
                <option value="" disabled>
                  Select a location *
                </option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Two-column row: Date & Time Slot */}
            <div className={styles.row}>
              <div className={styles.inputGroup}>
                <input
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split("T")[0]}
                  required
                />
              </div>
              <div className={styles.inputGroup}>
                <select
                  name="timeSlot"
                  value={form.timeSlot}
                  onChange={handleInputChange}
                  required
                >
                  <option value="" disabled>
                    Select a time slot *
                  </option>
                  {timeSlots.map((slot, index) => {
                    const bookedCount = bookedSlots[slot] || 0;
                    const isFull = bookedCount >= 2;
                    const isPast = handleTimeSlotDisable(slot);

                    return (
                      <option
                        key={index}
                        value={slot}
                        disabled={isFull || isPast}
                      >
                        {slot}{" "}
                        {isFull ? "(Full)" : isPast ? "(Past)" : "(Available)"}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            {/* Remarks */}
            <div className={styles.inputGroup}>
              <textarea
                name="remarks"
                value={form.remarks}
                onChange={handleInputChange}
                placeholder="Enter Remarks"
              ></textarea>
            </div>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ProductTrial;
